import React, { useState } from 'react';
import { Button, Input, DatePicker, Spin, Tabs, Modal } from 'antd';
import { useExchangeRateForm } from './hooks/useExchangeRateForm';
import EditTab from './components/EditTab';
import BrowseTab from './components/BrowseTab';
import dayjs from 'dayjs';
import './ExchangeRateForm.css';

const ExchangeRateForm: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, total, page, pageSize, current, currentIdx,
    form, setField, rateUnitValue,
    currencies, settings,
    searchCurrency, setSearchCurrency, searchDate, setSearchDate,
    selectRecord, doSearch, doPageChange,
    startAdd, startEdit, cancel, save, doDelete,
    handleCurrencyChange,
  } = useExchangeRateForm();

  const [activeTab, setActiveTab] = useState('edit');

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  // Double-click on browse grid → switch to edit tab
  const handleBrowseSelect = (idx: number) => {
    selectRecord(idx);
    setActiveTab('edit');
  };

  // Delete with confirmation
  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除 ${current.f10e_01} / ${current.f10e_02?.slice(0, 10)} 的匯率記錄？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  return (
    <Spin spinning={loading}>
      <div className="er-form">
        {/* Page Header */}
        <div className="page-header">
          <h2>
            匯率基本檔
            <span style={{ marginLeft: 12, fontSize: 13, fontWeight: 400, color: '#999' }}>
              [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${total}` : '無資料'}
            </span>
          </h2>
        </div>

        {/* Search Bar (BR-Q01) */}
        <div className="search-bar">
          <Input
            placeholder="幣別代號"
            value={searchCurrency}
            onChange={e => setSearchCurrency(e.target.value)}
            onPressEnter={doSearch}
            disabled={isEditing}
            style={{ width: 120 }}
          />
          <DatePicker
            placeholder="匯率日期"
            value={searchDate ? dayjs(searchDate) : null}
            onChange={d => setSearchDate(d ? d.format('YYYY-MM-DD') : '')}
            disabled={isEditing}
            format="YYYY-MM-DD"
            style={{ width: 160 }}
          />
          <Button onClick={doSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Action Bar */}
        <div className="action-bar">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>保存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>

        {/* Tabs: Edit + Browse */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'edit',
              label: '編輯',
              children: (
                <EditTab
                  mode={mode}
                  form={form}
                  current={current}
                  rateUnitValue={rateUnitValue}
                  currencies={currencies}
                  settings={settings}
                  setField={setField as (key: string, value: any) => void}
                  onCurrencyChange={handleCurrencyChange}
                />
              ),
            },
            {
              key: 'browse',
              label: '瀏覽',
              children: (
                <BrowseTab
                  records={records}
                  total={total}
                  page={page}
                  pageSize={pageSize}
                  currentIdx={currentIdx}
                  disabled={isEditing}
                  onSelect={handleBrowseSelect}
                  onPageChange={doPageChange}
                />
              ),
            },
          ]}
        />
      </div>
    </Spin>
  );
};

export default ExchangeRateForm;
